import {
  CheckIcon,
  ChevronRightIcon,
  ListFilterIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react"
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/primitives/input-group.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/primitives/select.tsx"
import { Switch } from "@/primitives/switch.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { useShortcut, useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { Kbd } from "./key.tsx"
import { chipsThatFit, chipValuesOf, describeChip, isOffered, operatorOf } from "./filter-bar.ts"
import { isSearchable, matching } from "./property.ts"
import { SearchRow } from "./property.tsx"
import { useTriggerTooltip } from "./use-trigger-tooltip.ts"

/*
 * A list's toolbar controls, one idiom on every page: Filter, which narrows the list by a facet and
 * leaves a removable chip saying so ("Role is Admin ×"); a search field; and Display, which holds
 * the view's options (order, grouping, what's included). None of them owns state: the page (or
 * the DataTable) keeps the view in the URL and passes it in, and hears every change.
 */

/**
 * One value a facet can take, as the Filter menu offers it. `count` is how many rows have it;
 * `keywords` are more words its search finds it by without showing them, as a Member's email.
 */
export type FacetOption = {
  id: string
  label: string
  glyph?: ReactNode
  secondary?: string
  keywords?: string
  count?: number
}

/**
 * One way to narrow a list: its values, the ones chosen, and whether one (`single`) or several
 * (`multi`, any of which passes) may be chosen at once. `labelOf` names a chosen value that isn't
 * among `values`, as one from an old link is. `narrowsWithOne` is a facet only some rows have a
 * value of, which the menu offers with one value alone.
 */
export type Facet = {
  id: string
  label: string
  icon: LucideIcon
  mode: "single" | "multi"
  values: readonly FacetOption[]
  chosen: readonly string[]
  onChange: (ids: readonly string[]) => void
  labelOf?: (id: string) => string
  narrowsWithOne?: boolean
}

/** Moves focus to the next or previous option in a menu with the arrow keys. */
function roveOptions(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
  const options = [...event.currentTarget.querySelectorAll<HTMLElement>("[data-option]")]
  if (options.length === 0) return
  event.preventDefault()
  const at = options.indexOf(document.activeElement as HTMLElement)
  const next =
    event.key === "ArrowDown"
      ? options[at < 0 ? 0 : Math.min(at + 1, options.length - 1)]
      : options[at < 0 ? options.length - 1 : Math.max(at - 1, 0)]
  next?.focus()
}

// A row in the Filter popover, highlighted in the popover's accent: the panel's own would vanish on
// a dark popover, and the keyboard's place in the list with it. On touch it is a menu row, 44px.
const optionClass =
  "flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-ui outline-none select-none transition-colors duration-100 ease-snappy hover:bg-popover-accent focus-visible:bg-popover-accent touch:min-h-11"

/**
 * The Filter button, the chips for every facet with a value chosen, and "Clear" after them. The
 * menu opens on the facets (F opens it too); choosing one lists its values, with a search once
 * there are more than seven, and each value's count. Backspace in an empty search goes back to the
 * facets. A facet with too few values to narrow anything isn't offered (`isOffered`), though its
 * chip stays while a value is chosen (from an old link, say). A chip's value reopens its facet,
 * and Esc clears every filter when nothing is open over the page.
 *
 * `onClear` clears every facet at once, which the page does in one change to its URL.
 */
export function FilterMenu({ facets, onClear }: { facets: readonly Facet[]; onClear: () => void }) {
  const [open, setOpen] = useState(false)
  const [level, setLevel] = useState<string | null>(null)
  const offered = facets.filter(isOffered)
  const chosen = facets.filter((facet) => facet.chosen.length > 0)
  const facet = facets.find(({ id }) => id === level)
  const fitting = useChipsThatFit(chosen.map(({ id }) => id).join(" "), chosen.length)
  // The chips with no room, each as it would say itself: "Epic is INS-6 Reporting".
  const folded = chosen
    .slice(fitting.fit)
    .map((each) =>
      describeChip(
        each.label,
        operatorOf(each.mode, each.chosen.length),
        chipValuesOf(each.values, each.chosen, each.labelOf),
      ),
    )
  const tip = useTriggerTooltip(open)

  const openAt = (id: string | null) => {
    setLevel(id)
    setOpen(true)
  }

  useShortcut("open-filter", () => openAt(null), { enabled: offered.length > 0 })
  useShortcuts({ "clear-filters": onClear }, { enabled: chosen.length > 0 })

  return (
    <>
      {offered.length > 0 && (
        <Popover
          open={open}
          onOpenChange={setOpen}
          onOpenChangeComplete={(isOpen) => !isOpen && setLevel(null)}
        >
          <Tooltip {...tip.tooltip}>
            <TooltipTrigger
              render={
                <PopoverTrigger
                  render={<Button variant="ghost" size="sm" onClick={() => setLevel(null)} />}
                />
              }
              {...tip.trigger}
            >
              <ListFilterIcon data-icon="inline-start" />
              Filter
            </TooltipTrigger>
            <TooltipContent>
              Filter
              <Kbd shortcut="open-filter" />
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            className="w-64 gap-0 p-1"
            title={facet ? facet.label : "Filter"}
          >
            {facet ? (
              <FacetValues
                key={facet.id}
                facet={facet}
                onBack={() => setLevel(null)}
                onChosen={() => setOpen(false)}
              />
            ) : (
              <div role="menu" aria-label="Filter by" onKeyDown={roveOptions}>
                {offered.map(({ id, label, icon: Icon }, index) => (
                  <button
                    key={id}
                    type="button"
                    role="menuitem"
                    data-option
                    autoFocus={index === 0}
                    className={optionClass}
                    onClick={() => setLevel(id)}
                  >
                    <Icon aria-hidden className="size-4 text-muted-foreground" />
                    <span className="truncate">{label}</span>
                    <ChevronRightIcon
                      aria-hidden
                      className="ml-auto size-3.5 text-subtle-foreground"
                    />
                  </button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
      {chosen.length > 0 && (
        // The chips give way before anything else in the toolbar: past what the row has room for,
        // those that don't fit whole fold into a "+n" chip after the rest, which opens the Filter
        // menu, so a Board with every facet set never runs under the search or Display, and every
        // chip can still be reached. Clear stays outside, never hidden. A folded chip stands out of
        // the row, unseen, where it is still measured, so it comes back as soon as it fits.
        <div ref={fitting.container} className="relative flex min-w-0 items-center gap-2">
          {chosen.map((each, index) => (
            <span
              key={each.id}
              className={cn(
                "flex shrink-0",
                index >= fitting.fit && "invisible absolute top-0 left-0",
              )}
            >
              <FilterChip
                field={{ label: each.label, icon: each.icon }}
                operator={operatorOf(each.mode, each.chosen.length)}
                values={chipValuesOf(each.values, each.chosen, each.labelOf)}
                onRemove={() => each.onChange([])}
                {...(offered.includes(each) && { onOpen: () => openAt(each.id) })}
              />
            </span>
          ))}
        </div>
      )}
      {folded.length > 0 && (
        // A chip itself, in the chips' fill and ring, naming the folded filters in its tooltip,
        // each as its chip would say it.
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                ref={fitting.more}
                type="button"
                aria-label={`${folded.length} more ${folded.length === 1 ? "filter" : "filters"}: ${folded.join("; ")}`}
                className="inline-flex h-6 shrink-0 items-center rounded-md bg-background px-2 text-xs text-muted-foreground tabular-nums ring-1 ring-border transition-colors duration-100 ease-snappy outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring compact:h-8"
                onClick={() => openAt(null)}
              />
            }
          >
            +{folded.length}
          </TooltipTrigger>
          <TooltipContent>
            <span className="flex flex-col">
              {folded.map((each) => (
                <span key={each}>{each}</span>
              ))}
            </span>
          </TooltipContent>
        </Tooltip>
      )}
      {chosen.length > 0 && (
        <Button variant="ghost" size="xs" onClick={onClear}>
          Clear
        </Button>
      )}
    </>
  )
}

/**
 * How many of the Filter's chips have room in the row it stands in, kept as the row, what else
 * stands in it, or the chips resize: `container` holds the chips, its parent the row, and `more`
 * is the "+n" chip while it shows. The room is the row's, less the other controls on the chips'
 * line, so a row whose width doesn't follow its chips (a toolbar's start, filling to its end; a
 * table's bar) never makes room for a chip by folding it. `chips` names the chips in force, so
 * the row is measured again when they change.
 */
function useChipsThatFit(chips: string, count: number) {
  const [fit, setFit] = useState(count)
  const container = useRef<HTMLDivElement>(null)
  const more = useRef<HTMLButtonElement>(null)
  useLayoutEffect(() => {
    const holder = container.current
    const row = holder?.parentElement
    if (!holder || !row || typeof ResizeObserver === "undefined") return
    const measure = () => {
      const line = holder.getBoundingClientRect()
      const middle = line.top + line.height / 2
      const rowStyle = getComputedStyle(row)
      const gap = parseFloat(rowStyle.columnGap) || 0
      const content =
        row.clientWidth - parseFloat(rowStyle.paddingLeft) - parseFloat(rowStyle.paddingRight)
      // What else stands on the chips' line: a wrapping row (a table's on `compact`) may put its
      // search on a line of its own.
      const others = itemsOf(row)
        .filter((item) => item !== holder && item !== more.current)
        .map((item) => item.getBoundingClientRect())
        .filter(({ top, bottom, width }) => width > 0 && top <= middle && middle <= bottom)
      const room = others.reduce((left, { width }) => left - width - gap, content)
      const widths = [...holder.children].map((chip) => chip.getBoundingClientRect().width)
      const plus = more.current?.getBoundingClientRect().width ?? 32
      setFit(
        chipsThatFit(widths, room, parseFloat(getComputedStyle(holder).columnGap) || 0, plus + gap),
      )
    }
    measure()
    const resized = new ResizeObserver(measure)
    resized.observe(row)
    for (const item of itemsOf(row)) resized.observe(item)
    for (const chip of holder.children) resized.observe(chip)
    return () => resized.disconnect()
  }, [chips])
  return { fit: Math.min(fit, count), container, more }
}

/** A row's items as its flex layout places them, those of a `display: contents` child among them. */
function itemsOf(row: Element): Element[] {
  return [...row.children].flatMap((child) =>
    getComputedStyle(child).display === "contents" ? itemsOf(child) : [child],
  )
}

/** A facet's values, the second level of the Filter menu. */
function FacetValues({
  facet,
  onBack,
  onChosen,
}: {
  facet: Facet
  onBack: () => void
  onChosen: () => void
}) {
  const [query, setQuery] = useState("")
  const searchable = isSearchable(facet.values)
  const shown = matching(facet.values, query)
  const multi = facet.mode === "multi"

  const toggle = (id: string) => {
    if (!multi) {
      facet.onChange(facet.chosen.includes(id) ? [] : [id])
      onChosen()
      return
    }
    facet.onChange(
      facet.chosen.includes(id)
        ? facet.chosen.filter((each) => each !== id)
        : [...facet.chosen, id],
    )
  }

  return (
    <div onKeyDown={roveOptions}>
      {searchable && (
        // The popover's padding is the menu's; the search runs edge to edge above it, as a
        // Picker's does.
        <div className="-mx-1 -mt-1 mb-1">
          <SearchRow
            autoFocus
            aria-label={`Search ${facet.label}`}
            placeholder={`${facet.label}…`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && query === "") onBack()
            }}
          />
        </div>
      )}
      <div
        role="menu"
        aria-label={facet.label}
        className="max-h-80 overflow-y-auto"
        onKeyDown={(event) => {
          if (event.key === "Backspace") onBack()
        }}
      >
        {shown.length === 0 ? (
          <p className="py-6 text-center text-ui text-subtle-foreground">No matches</p>
        ) : (
          shown.map((value, index) => {
            const checked = facet.chosen.includes(value.id)
            return (
              <button
                key={value.id}
                type="button"
                role={multi ? "menuitemcheckbox" : "menuitemradio"}
                aria-checked={checked}
                data-option
                autoFocus={!searchable && index === 0}
                className={optionClass}
                onClick={() => toggle(value.id)}
              >
                {multi ? (
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-3.5 shrink-0 items-center justify-center rounded-sm border",
                      checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-subtle-foreground bg-background",
                    )}
                  >
                    {checked && <CheckIcon className="size-3 stroke-3" />}
                  </span>
                ) : (
                  <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
                    {checked && <CheckIcon className="size-3.5 text-foreground" />}
                  </span>
                )}
                {value.glyph}
                <span className="truncate">
                  {value.label}
                  {value.secondary && (
                    <span className="ml-1.5 text-subtle-foreground">{value.secondary}</span>
                  )}
                </span>
                {value.count !== undefined && (
                  <span className="ml-auto pl-2 text-xs text-subtle-foreground tabular-nums">
                    {value.count}
                  </span>
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

const segment = "flex h-full items-center gap-1 px-1.5"

/**
 * One filter in force, in three parts: the field, the operator ("is", "is any of") and the value,
 * its glyphs then its first two names and "+N" for the rest; then × to remove it. `onOpen` makes
 * the value a button that reopens the facet's values. A chip for a view choice rather than a
 * facet, such as "Closed included", passes neither `field` nor `operator`, and is its value alone.
 * On `compact` it stands 32px tall, on the compact toolbar's row of chips.
 */
export function FilterChip({
  field,
  operator,
  values,
  onRemove,
  onOpen,
}: {
  field?: { label: string; icon: LucideIcon }
  operator?: "is" | "is any of"
  values: readonly { label: string; glyph?: ReactNode }[]
  onRemove: () => void
  onOpen?: () => void
}) {
  const named = values.slice(0, 2)
  const rest = values.length - named.length
  const value = (
    <>
      {values.some(({ glyph }) => glyph) && (
        <span className="flex items-center gap-0.5">
          {values
            .slice(0, 3)
            .map(({ glyph }, index) => (glyph ? <span key={index}>{glyph}</span> : null))}
        </span>
      )}
      <span className="max-w-40 truncate">
        {named.map(({ label }) => label).join(", ")}
        {rest > 0 && ` +${rest}`}
      </span>
    </>
  )
  const described = describeChip(field?.label, operator, values)
  return (
    <span
      role="group"
      aria-label={described}
      className="inline-flex h-6 shrink-0 items-center rounded-md bg-background text-xs ring-1 ring-border compact:h-8"
    >
      {field && (
        <span className={cn(segment, "text-muted-foreground")}>
          <field.icon aria-hidden className="size-3.5" />
          {field.label}
        </span>
      )}
      {operator && (
        <span className={cn(segment, "border-l border-border-subtle text-subtle-foreground")}>
          {operator}
        </span>
      )}
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            segment,
            field && "border-l border-border-subtle",
            "text-foreground transition-colors duration-100 ease-snappy outline-none hover:bg-accent focus-visible:bg-accent",
          )}
        >
          {value}
        </button>
      ) : (
        <span className={cn(segment, field && "border-l border-border-subtle", "text-foreground")}>
          {value}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Remove filter"
        onClick={onRemove}
        className="size-6 rounded-l-none border-l border-border-subtle compact:size-8"
      >
        <XIcon className="size-3" />
      </Button>
    </span>
  )
}

/**
 * The list's search: `/` focuses it from anywhere on the page, Esc empties it, and a second Esc
 * leaves it. The page debounces what it does with `onChange`, as its search needs.
 */
export function SearchField({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string
  value: string
  onChange: (value: string) => void
}) {
  const field = useRef<HTMLInputElement>(null)
  useShortcut("search", () => {
    field.current?.focus()
    field.current?.select()
  })
  return (
    <InputGroup className="h-7 w-56 min-w-40">
      <InputGroupAddon>
        <SearchIcon />
      </InputGroupAddon>
      <InputGroupInput
        ref={field}
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return
          if (value) onChange("")
          else event.currentTarget.blur()
        }}
      />
      <InputGroupAddon align="inline-end">
        {value ? (
          <InputGroupButton
            size="icon-xs"
            aria-label="Clear search"
            onClick={() => {
              onChange("")
              field.current?.focus()
            }}
          >
            <XIcon />
          </InputGroupButton>
        ) : (
          <span className="group-focus-within/input-group:hidden">
            <Kbd shortcut="search" />
          </span>
        )}
      </InputGroupAddon>
    </InputGroup>
  )
}

/**
 * One of a view's options in the Display menu: a choice among several, or on and off. A choice's
 * `icon` leads its row in the compact View sheet, as the Filter's does.
 */
export type DisplayRow = {
  label: string
  icon?: LucideIcon
  control:
    | {
        kind: "select"
        value: string
        options: readonly { value: string; label: string }[]
        onChange: (value: string) => void
      }
    | {
        kind: "switch"
        checked: boolean
        onChange: (checked: boolean) => void
        /** Why the option can't be changed in this view, such as "Only when grouped by Epic". */
        disabledReason?: string
      }
}

/**
 * The view's options, behind one button: order, grouping and what's included. A dot on the button
 * says the view isn't the default, so a Member whose list looks short can tell something is set. An
 * option that doesn't apply to the view as it is shows faded, its tooltip saying why, or on touch,
 * where a faded control can't be tapped for a tooltip, a line under it.
 */
export function DisplayMenu({
  rows,
  nonDefault,
}: {
  rows: readonly DisplayRow[]
  nonDefault: boolean
}) {
  return (
    <Popover>
      <PopoverTrigger render={<Button variant="outline" size="sm" className="relative" />}>
        <SlidersHorizontalIcon data-icon="inline-start" />
        Display
        {nonDefault && (
          <span
            aria-label="Changed from the default"
            className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-primary"
          />
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 gap-1 p-3" title="Display">
        {rows.map((row) => (
          <DisplayOption key={row.label} row={row} />
        ))}
      </PopoverContent>
    </Popover>
  )
}

function DisplayOption({ row: { label, control } }: { row: DisplayRow }) {
  if (control.kind === "select")
    return (
      <div className="flex h-8 items-center justify-between gap-3 text-ui touch:h-11">
        <span className="text-muted-foreground">{label}</span>
        <Select
          items={control.options}
          value={control.value}
          onValueChange={(value) => value !== null && control.onChange(value)}
        >
          <SelectTrigger size="sm" variant="ghost" aria-label={label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {control.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  const row = (
    <label
      className={cn(
        "flex h-8 items-center justify-between gap-3 text-ui touch:h-11",
        control.disabledReason && "opacity-50",
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <Switch
        checked={control.checked}
        disabled={Boolean(control.disabledReason)}
        onCheckedChange={(checked) => control.onChange(checked)}
      />
    </label>
  )
  if (!control.disabledReason) return row
  return (
    <div>
      <Tooltip>
        <TooltipTrigger render={row} />
        <TooltipContent>{control.disabledReason}</TooltipContent>
      </Tooltip>
      <p className="hidden pb-1 text-xs text-muted-foreground touch:block">
        {control.disabledReason}
      </p>
    </div>
  )
}
